import React from "react";
import { ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";

// Renders a clean, official legal document (Privacy Policy / Terms of Service)
// in the Carivisor dark-neon style. `sections` is an array of
// { heading, body: string[] | JSX }.
export default function LegalPage({
  eyebrow,
  title,
  intro,
  effectiveDate,
  sections,
  seoTitle,
  seoDescription,
  path,
  testid,
}) {
  return (
    <div className="min-h-screen bg-[#060810]" data-testid={testid}>
      <Seo title={seoTitle} description={seoDescription} path={path} />

      {/* Header */}
      <section className="border-b border-[#1D2640] bg-[#0B0E1B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <p className="font-mono-caption text-xs uppercase tracking-[0.32em] text-cyan-400">{eyebrow}</p>
          </div>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-tight">
            {title}
          </h1>
          {intro && <p className="text-slate-300 text-base md:text-lg mt-6 leading-relaxed max-w-3xl">{intro}</p>}
          <p className="text-xs text-slate-500 mt-6 font-mono-caption uppercase tracking-[0.2em]">
            Effective date: {effectiveDate}
          </p>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="space-y-12">
          {sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-heading font-bold text-white text-2xl mb-4 flex items-baseline gap-3">
                <span className="text-gradient-warm font-mono-caption text-lg">{String(i + 1).padStart(2, "0")}</span>
                {s.heading}
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed text-[15px]">
                {Array.isArray(s.body)
                  ? s.body.map((p, j) =>
                      typeof p === "string" ? <p key={j}>{p}</p> : <React.Fragment key={j}>{p}</React.Fragment>
                    )
                  : s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-[#1D2640] text-sm text-slate-500 leading-relaxed">
          <p>
            This document is provided for general informational purposes and does not constitute legal advice. We
            recommend reviewing it with qualified legal counsel before relying on it.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// Shared helper for a titled bullet list inside a legal section body.
export function LegalList({ items }) {
  return (
    <ul className="space-y-2.5 pl-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
